import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input, Tag, Typography } from 'antd';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { shipmentsApi, skladsApi } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { ExtraItemBody, ShipmentItem } from '../../api/types';
import { AmountInput } from '../../components/AmountInput';
import { ModalCombobox } from '../../components/ModalCombobox';
import { ModalSelect } from '../../components/ModalSelect';
import { formatKopeks, isValidRubInput, kopeksToRubString, rubToKopeks } from '../../lib/money';
import { normalizeName } from '../../lib/productName';
import { formatQuantityMilli, isValidQtyInput, qtyToMilli } from '../../lib/quantity';

/** One owner-added line (raw strings — string math only, SPEC §2). */
export interface ExtraRow {
  name?: string;
  qty?: string;
  buy?: string;
  sell?: string;
}
/** Owner-added items grouped by the sklad they came from. */
export interface ExtraGroup {
  skladId: string;
  rows: ExtraRow[];
}

const parseQty = (row: ExtraRow): number | null =>
  row.qty && isValidQtyInput(row.qty) && qtyToMilli(row.qty) > 0 ? qtyToMilli(row.qty) : null;
const parsePrice = (value?: string): number | null =>
  value && value !== '-' && isValidRubInput(value) ? rubToKopeks(value) : null;
const lineSum = (quantityMilli: number, priceKopeks: number): number =>
  Math.round((quantityMilli * priceKopeks) / 1000);

/** A row counts once name, qty, buy AND sell are all valid. */
function rowComplete(row: ExtraRow): boolean {
  return (
    !!row.name?.trim() && parseQty(row) != null && parsePrice(row.buy) != null && parsePrice(row.sell) != null
  );
}

/** Every group non-empty and every row fully filled — the post gate. */
export function extraGroupsComplete(groups: ExtraGroup[]): boolean {
  return groups.every((g) => g.rows.length > 0 && g.rows.every(rowComplete));
}

/** Sum of buy (or sell) line totals across all complete rows. */
export function extraSideTotal(groups: ExtraGroup[], side: 'buy' | 'sell'): number {
  let sum = 0;
  for (const g of groups) {
    for (const row of g.rows) {
      const qty = parseQty(row);
      const price = parsePrice(row[side]);
      if (qty != null && price != null) sum += lineSum(qty, price);
    }
  }
  return sum;
}

/** Groups → API items (complete rows only). */
export function extraGroupsToItems(groups: ExtraGroup[]): ExtraItemBody[] {
  const items: ExtraItemBody[] = [];
  for (const g of groups) {
    for (const row of g.rows) {
      if (!rowComplete(row)) continue;
      items.push({
        skladId: g.skladId,
        name: row.name!.trim(),
        quantityMilli: qtyToMilli(row.qty!),
        buyPriceKopeks: rubToKopeks(row.buy!),
        issuePriceKopeks: rubToKopeks(row.sell!),
      });
    }
  }
  return items;
}

/** Seed groups from a shipment's existing MANUAL-origin items. */
export function extraGroupsFromItems(items: ShipmentItem[]): ExtraGroup[] {
  const byS = new Map<string, ExtraRow[]>();
  for (const item of items) {
    if (item.origin !== 'MANUAL' || !item.skladId) continue;
    const row: ExtraRow = {
      name: item.name,
      qty: formatQuantityMilli(item.quantityMilli),
      buy: item.buyPriceKopeks != null ? kopeksToRubString(item.buyPriceKopeks) : '',
      sell: item.issuePriceKopeks != null ? kopeksToRubString(item.issuePriceKopeks) : '',
    };
    const rows = byS.get(item.skladId);
    if (rows) rows.push(row);
    else byS.set(item.skladId, [row]);
  }
  return [...byS.entries()].map(([skladId, rows]) => ({ skladId, rows }));
}

/**
 * Owner-added items grouped by sklad — the «+ со склада» section shared by the
 * MoySklad pricing screen and the manual editor. Controlled: `value`/`onChange`
 * carry the groups; a fully-typed row auto-fills its prices from store memory.
 */
export function ExtraSkladItems({
  value,
  onChange,
  storeId,
}: {
  value: ExtraGroup[];
  onChange: (groups: ExtraGroup[]) => void;
  storeId?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const skladsQuery = useQuery({ queryKey: queryKeys.sklads(), queryFn: () => skladsApi.list() });
  const productNamesQuery = useQuery({
    queryKey: queryKeys.shipmentProductNames,
    queryFn: () => shipmentsApi.productNames(),
    staleTime: 5 * 60_000,
  });
  const lastPricesQuery = useQuery({
    queryKey: queryKeys.shipmentLastPrices(storeId ?? ''),
    queryFn: () => shipmentsApi.lastPrices(storeId!),
    enabled: !!storeId,
    staleTime: 60_000,
  });

  const skladName = (id: string): string =>
    skladsQuery.data?.find((s) => s.id === id)?.name ?? id;

  const patchRow = (gi: number, ri: number, patch: Partial<ExtraRow>): void =>
    onChange(
      value.map((g, i) =>
        i === gi ? { ...g, rows: g.rows.map((r, j) => (j === ri ? { ...r, ...patch } : r)) } : g,
      ),
    );
  const addRow = (gi: number): void =>
    onChange(value.map((g, i) => (i === gi ? { ...g, rows: [...g.rows, {}] } : g)));
  const removeRow = (gi: number, ri: number): void =>
    onChange(value.map((g, i) => (i === gi ? { ...g, rows: g.rows.filter((_, j) => j !== ri) } : g)));
  const removeGroup = (gi: number): void => onChange(value.filter((_, i) => i !== gi));
  const addGroup = (skladId: string): void => onChange([...value, { skladId, rows: [{}] }]);

  // Commit a picked/typed product name AND auto-fill its remembered buy+sell in
  // ONE patch. Doing it as two separate patchRow calls loses the name: both derive
  // from the same stale `value` closure, so the second setExtraGroups (prices,
  // built from the pre-name array) overwrites the first (name).
  const commitName = (gi: number, ri: number, rawName: string): void => {
    const name = rawName.trim();
    const patch: Partial<ExtraRow> = { name };
    const mem = lastPricesQuery.data?.[normalizeName(name)];
    const row = value[gi]?.rows[ri];
    if (mem && row) {
      if (!row.buy && mem.buyPriceKopeks != null) patch.buy = kopeksToRubString(mem.buyPriceKopeks);
      if (!row.sell && mem.issuePriceKopeks != null) patch.sell = kopeksToRubString(mem.issuePriceKopeks);
    }
    patchRow(gi, ri, patch);
  };

  const usedSkladIds = new Set(value.map((g) => g.skladId));
  const availableSklads = (skladsQuery.data ?? []).filter((s) => !usedSkladIds.has(s.id));

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {value.map((group, gi) => (
        <Card
          key={group.skladId}
          size="small"
          // Each sklad group posts as its own накладная (its own leg) — a bolder
          // teal border + light tint separates it clearly from the others.
          style={{ borderColor: '#0e7490', borderWidth: 2, background: '#f3fafb' }}
          styles={{ header: { background: '#e4f4f7' } }}
          title={t('shipments.extraGroupTitle', { sklad: skladName(group.skladId) })}
          extra={
            <Button
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => removeGroup(gi)}
              aria-label={t('common.delete')}
            />
          }
        >
          <div style={{ display: 'grid', gap: 12 }}>
            {group.rows.map((row, ri) => {
              const qty = parseQty(row);
              const buy = parsePrice(row.buy);
              const sell = parsePrice(row.sell);
              return (
                <div key={ri} style={{ display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Typography.Text
                      type="secondary"
                      strong
                      style={{ minWidth: 20, textAlign: 'right' }}
                    >
                      {ri + 1}
                    </Typography.Text>
                    <ModalCombobox
                      style={{ flex: 1 }}
                      size="middle"
                      value={row.name}
                      options={productNamesQuery.data ?? []}
                      placeholder={t('shipments.itemNamePlaceholder')}
                      title={t('shipments.itemNamePlaceholder')}
                      maxLength={300}
                      onChange={(v) => commitName(gi, ri, v)}
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => removeRow(gi, ri)}
                      disabled={group.rows.length === 1}
                      aria-label={t('common.delete')}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 1fr', gap: 8 }}>
                    <Input
                      inputMode="decimal"
                      placeholder="1,5"
                      autoComplete="off"
                      value={row.qty ?? ''}
                      onChange={(e) => patchRow(gi, ri, { qty: e.target.value })}
                    />
                    <AmountInput
                      value={row.buy ?? ''}
                      onChange={(v) => patchRow(gi, ri, { buy: v })}
                    />
                    <AmountInput
                      value={row.sell ?? ''}
                      onChange={(v) => patchRow(gi, ri, { sell: v })}
                    />
                  </div>
                  {qty != null && (buy != null || sell != null) ? (
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      {buy != null ? `${t('shipments.buyShort')}: ${formatKopeks(lineSum(qty, buy))}` : ''}
                      {buy != null && sell != null ? ' · ' : ''}
                      {sell === 0 && (buy ?? 0) > 0 ? (
                        <Tag color="orange" style={{ margin: '0 0 0 4px' }}>
                          {t('shipments.freeTag')}
                        </Tag>
                      ) : sell != null ? (
                        `${t('shipments.colMySum')}: ${formatKopeks(lineSum(qty, sell))}`
                      ) : (
                        ''
                      )}
                    </Typography.Text>
                  ) : null}
                </div>
              );
            })}
            <Button icon={<PlusOutlined />} onClick={() => addRow(gi)} block>
              {t('shipments.addItem')}
            </Button>
            {(() => {
              const groupBuy = extraSideTotal([group], 'buy');
              const groupSell = extraSideTotal([group], 'sell');
              return groupBuy > 0 || groupSell > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 8,
                    borderTop: '1px solid #d0e7ec',
                    paddingTop: 8,
                  }}
                >
                  <Typography.Text strong>{t('shipments.extraGroupTotal')}</Typography.Text>
                  <Typography.Text strong style={{ textAlign: 'right' }}>
                    {t('shipments.buyShort')}: {formatKopeks(groupBuy)}
                    {' · '}
                    {t('shipments.colMySum')}: {formatKopeks(groupSell)}
                  </Typography.Text>
                </div>
              ) : null;
            })()}
          </div>
        </Card>
      ))}

      <ModalSelect
        value={null}
        placeholder={t('shipments.addFromSklad')}
        title={t('shipments.addFromSklad')}
        size="middle"
        style={{ width: '100%' }}
        loading={skladsQuery.isLoading}
        options={availableSklads.map((s) => ({ value: s.id, label: s.name }))}
        onSelect={(id) => addGroup(id)}
        suffixIcon={<PlusOutlined />}
      />
    </div>
  );
}
